import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Save, Users, FileText, X } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'



import type { Ministerio } from '@/types'

interface MinisterioEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ministerio: Ministerio | null
  onSave: (ministerio: Ministerio) => void
}

export function MinisterioEditModal({ open, onOpenChange, ministerio, onSave }: MinisterioEditModalProps) {
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    grupoFuncoes: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Atualizar form data quando o ministério mudar
  useEffect(() => {
    if (ministerio) {
      setFormData({
        nome: ministerio.nome || '',
        descricao: ministerio.descricao || '',
        grupoFuncoes: ministerio.grupoFuncoes || 'outros'
      })
    }
  }, [ministerio])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nome.trim() || !formData.grupoFuncoes || !ministerio) {
      return
    }

    setIsSubmitting(true)
    
    try {
      const ministerioAtualizado: Ministerio = {
        ...ministerio,
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim() || null,
        grupoFuncoes: formData.grupoFuncoes,
        updated_at: new Date().toISOString()
      }

      await onSave(ministerioAtualizado)
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao atualizar ministério:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!ministerio) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Editar Ministério
          </DialogTitle>
          <DialogDescription>
            Atualize as informações do ministério {ministerio.nome}
          </DialogDescription>
        </DialogHeader>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Nome do Ministério */}
          <div className="space-y-2">
            <Label htmlFor="nome" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Nome do Ministério
            </Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex: Ministério de Louvor"
              required
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="descricao" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Descrição
            </Label>
            <Textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
              placeholder="Descreva o propósito e atividades do ministério..."
              rows={3}
            />
          </div>

          {/* Botões */}
          <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.nome.trim() || !formData.grupoFuncoes}
              className="w-full sm:w-auto"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </motion.form>
      </DialogContent>
    </Dialog>
  )
}